import * as React from 'react'
import { ButtonBase, useForkRef } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import CancelIcon from '@mui/icons-material/Cancel'
import { chipColorStyles } from './DataDisplay.styles'

function isDeleteKeyboardEvent(keyboardEvent) {
  return keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete'
}

const StyledChip = styled('div')(({ theme, color, size, variant, clickable, onDelete }) => ({
  ...chipColorStyles(theme),
  ...(color !== 'default' && {
    backgroundColor: theme.palette[color]?.main,
    color: theme.palette[color]?.contrastText
  }),
  ...(clickable && {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette[color]?.dark
    }
  }),
  ...(onDelete && {
    paddingRight: theme.spacing(1)
  }),
  ...(size === 'small' && {
    height: theme.spacing(3),
    fontSize: theme.typography.pxToRem(12)
  }),
  ...(variant === 'outlined' && {
    border: `1px solid ${theme.palette[color]?.main}`,
    backgroundColor: 'transparent',
    color: theme.palette[color]?.main
  })
}))

const ChipColor = React.forwardRef(function Chip(props, ref) {
  const {
    avatar: avatarProp,
    className,
    clickable: clickableProp,
    color = 'default',
    deleteIcon: deleteIconProp,
    icon: iconProp,
    label,
    onClick,
    onDelete,
    onKeyDown,
    onKeyUp,
    size = 'medium',
    variant = 'default',
    ...other
  } = props

  const chipRef = React.useRef(null)
  const handleRef = useForkRef(chipRef, ref)

  const handleDeleteIconClick = event => {
    event.stopPropagation()
    if (onDelete) {
      onDelete(event)
    }
  }

  const handleKeyDown = event => {
    if (event.currentTarget === event.target && isDeleteKeyboardEvent(event)) {
      event.preventDefault()
    }
    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  const handleKeyUp = event => {
    if (event.currentTarget === event.target) {
      if (onDelete && isDeleteKeyboardEvent(event)) {
        onDelete(event)
      } else if (event.key === 'Escape' && chipRef.current) {
        chipRef.current.blur()
      }
    }
    if (onKeyUp) {
      onKeyUp(event)
    }
  }

  const clickable = clickableProp !== false && onClick ? true : clickableProp

  const Component = clickable ? ButtonBase : 'div'
  const moreProps = Component === ButtonBase ? { component: 'div' } : {}

  let deleteIcon = null
  if (onDelete) {
    deleteIcon = deleteIconProp ? (
      React.cloneElement(deleteIconProp, {
        onClick: handleDeleteIconClick
      })
    ) : (
      <CancelIcon onClick={handleDeleteIconClick} />
    )
  }

  return (
    <StyledChip
      as={Component}
      ref={handleRef}
      role={clickable || onDelete ? 'button' : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      className={clsx(className)}
      color={color}
      size={size}
      variant={variant}
      clickable={clickable}
      onDelete={onDelete}
      {...moreProps}
      {...other}
    >
      {avatarProp}
      {iconProp}
      <span>{label}</span>
      {deleteIcon}
    </StyledChip>
  )
})

ChipColor.propTypes = {
  avatar: PropTypes.element,
  children: PropTypes.node,
  className: PropTypes.string,
  clickable: PropTypes.bool,
  color: PropTypes.oneOf(['default', 'success', 'secondary']),
  component: PropTypes.elementType,
  deleteIcon: PropTypes.element,
  disabled: PropTypes.bool,
  icon: PropTypes.element,
  label: PropTypes.node,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  size: PropTypes.oneOf(['medium', 'small']),
  variant: PropTypes.oneOf(['default', 'outlined'])
}

export default ChipColor
